/**
 * Сессия и права на клиенте.
 *
 * Критически важная оговорка: список прав, приходящий из /api/auth/me,
 * используется ТОЛЬКО для того, чтобы решить, что рисовать. Он ничего не
 * разрешает. Каждый запрос всё равно проходит полную проверку на сервере, и
 * клиент обязан корректно пережить 403 на действие, которое по его
 * представлению доступно.
 *
 * Обратное — «раз право есть в списке, можно не проверять» — и есть та самая
 * ошибка, из-за которой скрытие кнопок начинают считать защитой.
 */

import { purgeOfflineData } from '../lib/pwa.ts';
import { disablePush } from '../lib/push.ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CurrentUserDto } from '@tw/shared-types';
import { ApiClient, ApiError } from '../lib/api.js';

interface SessionState {
  user: CurrentUserDto | null;
  loading: boolean;
  error: string | null;
}

interface SessionContextValue extends SessionState {
  api: ApiClient;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Есть ли у роли право. Только для отрисовки UI. */
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    user: null,
    loading: true,
    error: null,
  });

  // Клиент создаётся один раз: onUnauthorized замыкается на setState, и
  // пересоздание на каждый рендер обнуляло бы обработчик.
  const api = useMemo(
    () =>
      new ApiClient({
        onUnauthorized: () => {
          setState({ user: null, loading: false, error: null });
        },
      }),
    [],
  );

  const refresh = useCallback(async () => {
    try {
      const user = await api.me();
      setState({ user, loading: false, error: null });
    } catch (error) {
      // 401 здесь ожидаем: это просто «не вошёл», а не сбой.
      const message =
        error instanceof ApiError && error.isUnauthorized
          ? null
          : error instanceof Error
            ? error.message
            : 'Не удалось загрузить профиль';
      setState({ user: null, loading: false, error: message });
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      // Без loading: true. Флаг загрузки переключает App на заставку, форма
      // входа размонтируется и теряет введённый email — после неверного
      // пароля его приходилось набирать заново. Ожидание показывает сама
      // форма (submitting).
      setState((s) => ({ ...s, error: null }));
      // Данные прежнего сотрудника на этом телефоне — стереть до входа нового.
      purgeOfflineData();
      try {
        const { user } = await api.login(email, password);
        setState({ user, loading: false, error: null });
      } catch (error) {
        setState({
          user: null,
          loading: false,
          // Сервер намеренно не различает «нет такого email» и «неверный
          // пароль»; повторять его формулировку — правильно.
          error: error instanceof ApiError ? error.message : 'Не удалось войти',
        });
        throw error;
      }
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      // Пуши этого телефона — снять до выхода: после выхода запрос отписки
      // уже не пройдёт, а телефон продолжал бы получать чужие сообщения.
      await Promise.race([disablePush(api), new Promise((r) => setTimeout(r, 3000))]);
      await api.logout();
    } finally {
      // Даже если запрос не прошёл, локально считаем сессию завершённой:
      // оставить пользователя в интерфейсе после нажатия «выйти» хуже.
      // Сохранённые для офлайна чаты и брони — стереть с телефона.
      purgeOfflineData();
      setState({ user: null, loading: false, error: null });
    }
  }, [api]);

  const can = useCallback(
    (permission: string) => state.user?.permissions.includes(permission) ?? false,
    [state.user],
  );

  const value = useMemo(
    () => ({ ...state, api, login, logout, refresh, can }),
    [state, api, login, logout, refresh, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession вызван вне SessionProvider');
  return context;
}

/** Права текущего пользователя. Напоминание: только для отрисовки. */
export function usePermission(permission: string): boolean {
  return useSession().can(permission);
}

export function useCurrentUser(): CurrentUserDto {
  const { user } = useSession();
  if (!user) throw new Error('useCurrentUser вызван без активной сессии');
  return user;
}

/**
 * Обёртка «показать, если есть право».
 *
 * Существует, чтобы условная отрисовка выглядела единообразно и её было легко
 * найти при аудите: `grep -r "<IfAllowed"` даёт полный список мест, где UI
 * зависит от прав.
 */
export function IfAllowed({
  permission,
  children,
  fallback = null,
}: {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return usePermission(permission) ? <>{children}</> : <>{fallback}</>;
}
